/**
 * The family's canonical skeleton — what the Package door hands World.
 *
 * World binds a family clip by NAME and writes each keyed quaternion straight
 * into the node's local rotation (VRMAvatarDriver `bindAnimationDirect`, the
 * NPC binder is its twin): no retarget, no rest-relative transport. Two things
 * therefore have to be true of a packaged body for the family's clips to play
 * on it as they play on the anchor:
 *
 *   1. NAMES — every bone of the family's `bodySignature` is a skin joint
 *      (World's gate refuses the family otherwise: the body loads frozen), and
 *      every keyed bone carries the family's name for its slot.
 *   2. FRAMES — each keyed bone's local frame follows the anchor's convention:
 *      the child offset expressed in the bone's own frame points the way the
 *      anchor's does, and the roll matches. A body with the right names and
 *      another roll (Tripo's rig presets: +X-forward, the family's anchor is
 *      native Tripo +Z-forward) passes the gate and TWISTS silently.
 *
 * `planFamilyRename` answers (1) from the session's BBSA mapping;
 * `canonicalizeForFamily` performs (1) and (2) on the written GLB:
 *
 *   - rename: the rig's bones take the family's names through the BBSA slot
 *     (`bbsaToFamily`), the skeleton's own root joint above the hips becomes
 *     `Root` (ActorCore's `BoneRoot`);
 *   - supply: a signature bone the family NEVER keys (Root, Pelvis,
 *     NeckTwist02, the twelve twists — `keyed` says which) and the body lacks
 *     is added as a zero-weight joint at the anchor's local rest, placed
 *     proportionally; a missing KEYED bone refuses — the family animates it,
 *     so it must be the body's own flesh;
 *   - re-seat: every family bone's WORLD bind rotation becomes the anchor's,
 *     carried by the rotation that takes the anchor's limb frame (bone
 *     direction + the flesh's forward) onto this body's (`limbFrameMap`);
 *     unkeyed bones inherit their parent's so their local rest is the
 *     anchor's exactly — the family leaves them at rest. A body that already
 *     speaks the convention (native Tripo: median re-seat 1.5°) keeps its own
 *     frames — it plays today as the field accepted it (`convention`). Positions never move
 *     (except `Root`, seated on the ground below the hips so World's
 *     hip-height ratio reads the body's real hip height); inverse binds are
 *     recomputed so the mesh at rest is unchanged;
 *   - carry: every non-family node keeps its world transform (tails, eyes,
 *     share bones, tip markers) — only its local is re-expressed under the
 *     re-seated parent — and a `VRMC_springBone` collider riding a re-seated
 *     bone has its offsets re-expressed in the new frame, so the spring sees
 *     the same shapes in the same places. Every other byte of the extension
 *     (joints, groups, `extras`) passes through untouched.
 *
 * Engine-free: bytes in, bytes + report out.
 *
 * Depends on: math (quat, vec3), family-anchor-tripo-2026-handed. Depended on
 * by: the app's package door and its panel (the names the family is detected
 * on), tests.
 */
import { quatAngleBetween, quatConjugate, quatFromBasis, quatMultiply, quatNormalize, type Quat } from '../math/quat.js';
import { vec3Cross, vec3Dot, vec3Length, vec3Normalize, vec3Sub, type Vec3Data } from '../math/vec3.js';
import { KernelError } from '../ir/errors.js';
import { TRIPO_2026_HANDED_ANCHOR, TRIPO_2026_HANDED_KEYED, type AnchorRow } from './family-anchor-tripo-2026-handed.js';

export class FamilyCanonRefusal extends KernelError {
  constructor(readonly reasons: readonly string[]) {
    super(`the body cannot take the family's canonical skeleton:\n${reasons.map((r) => `  - ${r}`).join('\n')}`);
  }
}

export interface FamilyCanon {
  /** World's family id. */
  readonly id: string;
  /** The rest skeleton the family's clips were baked on (the clips carry it). */
  readonly anchor: readonly AnchorRow[];
  /** Bones the family's clips write a rotation to. */
  readonly keyed: readonly string[];
  /** BBSA ontology id → the family's name, where they differ (digits are spelled alike). */
  readonly bbsaToFamily: Readonly<Record<string, string>>;
}

const SIDED_TRIPO: Readonly<Record<string, string>> = {
  Shoulder: 'Clavicle',
  Arm: 'Upperarm',
  ForeArm: 'Forearm',
  Hand: 'Hand',
  UpLeg: 'Thigh',
  Leg: 'Calf',
  Foot: 'Foot',
  ToeBase: 'ToeBase',
};

export const TRIPO_2026_HANDED_CANON: FamilyCanon = {
  id: 'tripo-2026-handed',
  anchor: TRIPO_2026_HANDED_ANCHOR,
  keyed: TRIPO_2026_HANDED_KEYED,
  bbsaToFamily: {
    Hips: 'Hip',
    Spine: 'Waist',
    Spine1: 'Spine01',
    Spine2: 'Spine02',
    Neck: 'NeckTwist01',
    Head: 'Head',
    ...Object.fromEntries(
      Object.entries(SIDED_TRIPO).flatMap(([bbsa, tripo]) => [
        [`Left${bbsa}`, `L_${tripo}`],
        [`Right${bbsa}`, `R_${tripo}`],
      ]),
    ),
  },
};

/** The canons Harry can package for, by World family id. */
export const FAMILY_CANONS: Readonly<Record<string, FamilyCanon>> = { [TRIPO_2026_HANDED_CANON.id]: TRIPO_2026_HANDED_CANON };

const DIGIT = /^(Left|Right)Hand(Thumb|Index|Middle|Ring|Pinky)\d+$/;

/**
 * The family's body signature from its anchor — the same classes World's
 * manifest writer removes (`app/src/bake/body-signature.ts`): digits (only the
 * augmented variant has them), `_synth` tip markers, `_corr` helpers and the
 * `neutral_bone` exporter artifact.
 */
export function canonSignature(canon: FamilyCanon): string[] {
  return canon.anchor
    .map(([name]) => name)
    .filter((n) => !DIGIT.test(n) && !n.endsWith('_synth') && !n.endsWith('_corr') && n !== 'neutral_bone')
    .sort();
}

const stripGraft = (n: string): string => n.replace(/_graft$/, '');

export interface CanonBoneInput {
  /** The name as the FILE spells it (a session `_graft` suffix is stripped here). */
  readonly name: string;
  readonly parent: string | null;
}

export interface FamilyRenamePlan {
  readonly family: string;
  /** File name → family name, only the names that change. */
  readonly rename: ReadonlyMap<string, string>;
  /** Signature bones the body lacks that the family never keys — supplied as zero-weight joints. */
  readonly supplied: readonly string[];
  /** The skin-joint names the canonical body will carry (renamed + supplied). */
  readonly names: readonly string[];
  /** Why the body cannot take the family's skeleton — empty when it can. */
  readonly refusals: readonly string[];
}

/**
 * Which family name each bone takes, which signature bones are supplied, and
 * what refuses. `ontologyOf` is the session's BBSA mapping (rig name → id).
 */
export function planFamilyRename(
  bones: readonly CanonBoneInput[],
  ontologyOf: ReadonlyMap<string, string>,
  canon: FamilyCanon,
): FamilyRenamePlan {
  const anchorNames = new Set(canon.anchor.map(([n]) => n));
  const fileNames = bones.map((b) => stripGraft(b.name));
  const present = new Set(fileNames);
  const rename = new Map<string, string>();
  const refusals: string[] = [];
  const claimed = new Map<string, string>();
  const claim = (from: string, to: string): void => {
    const prior = claimed.get(to);
    if (prior !== undefined && prior !== from) {
      refusals.push(`"${from}" and "${prior}" both take the family name "${to}"`);
      return;
    }
    claimed.set(to, from);
    if (from !== to) rename.set(from, to);
  };
  for (const b of bones) {
    const name = stripGraft(b.name);
    if (anchorNames.has(name)) {
      claim(name, name);
      continue;
    }
    const id = ontologyOf.get(b.name) ?? ontologyOf.get(name);
    const family = id === undefined ? undefined : (canon.bbsaToFamily[id] ?? (anchorNames.has(id) ? id : undefined));
    if (family !== undefined) claim(name, family);
  }
  // The skeleton's own root joint above the hips is the family's `Root`
  // (ActorCore spells it `BoneRoot`) — when the body has no `Root` of its own.
  if (!claimed.has('Root')) {
    const hipsFile = claimed.get('Hip');
    const hips = hipsFile === undefined ? undefined : bones.find((b) => stripGraft(b.name) === hipsFile);
    const parent = hips?.parent === null || hips?.parent === undefined ? null : stripGraft(hips.parent);
    if (parent !== null && present.has(parent) && ![...claimed.values()].includes(parent)) claim(parent, 'Root');
  }
  const keyed = new Set(canon.keyed);
  const supplied: string[] = [];
  for (const bone of canonSignature(canon)) {
    if (claimed.has(bone)) continue;
    if (keyed.has(bone)) refusals.push(`the family animates "${bone}" and this rig has no bone for it`);
    else supplied.push(bone);
  }
  const names = [...fileNames.map((n) => rename.get(n) ?? n), ...supplied];
  return { family: canon.id, rename, supplied, names, refusals };
}

// ───────────────────────────── the GLB ─────────────────────────────

const GLB_MAGIC = 0x46546c67;
const CHUNK_JSON = 0x4e4f534a;
const CHUNK_BIN = 0x004e4942;

type Json = Record<string, unknown>;
interface GltfNode {
  name?: string;
  children?: number[];
  translation?: number[];
  rotation?: number[];
  scale?: number[];
  matrix?: number[];
  mesh?: number;
  skin?: number;
}
interface GltfAccessor {
  bufferView?: number;
  byteOffset?: number;
  componentType: number;
  count: number;
  type: string;
  min?: number[];
  max?: number[];
}
interface GltfBufferView {
  buffer: number;
  byteOffset?: number;
  byteLength: number;
  byteStride?: number;
}
interface GltfSkin {
  joints: number[];
  inverseBindMatrices?: number;
  skeleton?: number;
}

function parseGlb(glb: ArrayBuffer): { json: Json; bin: Uint8Array } {
  const view = new DataView(glb);
  if (glb.byteLength < 20 || view.getUint32(0, true) !== GLB_MAGIC) throw new FamilyCanonRefusal(['not a GLB (bad magic)']);
  let cursor = 12;
  let json: Json | null = null;
  let bin: Uint8Array = new Uint8Array(0);
  while (cursor + 8 <= glb.byteLength) {
    const length = view.getUint32(cursor, true);
    const type = view.getUint32(cursor + 4, true);
    const data = new Uint8Array(glb, cursor + 8, length);
    if (type === CHUNK_JSON) json = JSON.parse(new TextDecoder().decode(data)) as Json;
    else if (type === CHUNK_BIN) bin = new Uint8Array(data);
    cursor += 8 + length + ((4 - (length % 4)) % 4);
  }
  if (json === null) throw new FamilyCanonRefusal(['the GLB has no JSON chunk']);
  return { json, bin };
}

function buildGlb(json: Json, bin: Uint8Array): ArrayBuffer {
  const jsonBytes = new TextEncoder().encode(JSON.stringify(json));
  const jsonPad = (4 - (jsonBytes.length % 4)) % 4;
  const binPad = (4 - (bin.length % 4)) % 4;
  const total = 12 + 8 + jsonBytes.length + jsonPad + (bin.length > 0 ? 8 + bin.length + binPad : 0);
  const out = new ArrayBuffer(total);
  const dv = new DataView(out);
  const u8 = new Uint8Array(out);
  dv.setUint32(0, GLB_MAGIC, true);
  dv.setUint32(4, 2, true);
  dv.setUint32(8, total, true);
  dv.setUint32(12, jsonBytes.length + jsonPad, true);
  dv.setUint32(16, CHUNK_JSON, true);
  u8.set(jsonBytes, 20);
  u8.fill(0x20, 20 + jsonBytes.length, 20 + jsonBytes.length + jsonPad);
  if (bin.length > 0) {
    const at = 20 + jsonBytes.length + jsonPad;
    dv.setUint32(at, bin.length + binPad, true);
    dv.setUint32(at + 4, CHUNK_BIN, true);
    u8.set(bin, at + 8);
  }
  return out;
}

// ───────────────────────────── 4×4, column-major (glTF) ─────────────────────────────

type Mat4 = Float64Array;

function compose(t: Vec3Data, q: Quat, s: Vec3Data): Mat4 {
  const { x, y, z, w } = q;
  const x2 = x + x;
  const y2 = y + y;
  const z2 = z + z;
  const xx = x * x2;
  const xy = x * y2;
  const xz = x * z2;
  const yy = y * y2;
  const yz = y * z2;
  const zz = z * z2;
  const wx = w * x2;
  const wy = w * y2;
  const wz = w * z2;
  const m = new Float64Array(16);
  m[0] = (1 - (yy + zz)) * s.x;
  m[1] = (xy + wz) * s.x;
  m[2] = (xz - wy) * s.x;
  m[4] = (xy - wz) * s.y;
  m[5] = (1 - (xx + zz)) * s.y;
  m[6] = (yz + wx) * s.y;
  m[8] = (xz + wy) * s.z;
  m[9] = (yz - wx) * s.z;
  m[10] = (1 - (xx + yy)) * s.z;
  m[12] = t.x;
  m[13] = t.y;
  m[14] = t.z;
  m[15] = 1;
  return m;
}

function mul(a: Mat4, b: Mat4): Mat4 {
  const o = new Float64Array(16);
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      o[c * 4 + r] = a[r]! * b[c * 4]! + a[4 + r]! * b[c * 4 + 1]! + a[8 + r]! * b[c * 4 + 2]! + a[12 + r]! * b[c * 4 + 3]!;
    }
  }
  return o;
}

function invert(m: Mat4): Mat4 {
  const a = (i: number): number => m[i]!;
  const b00 = a(0) * a(5) - a(1) * a(4);
  const b01 = a(0) * a(6) - a(2) * a(4);
  const b02 = a(0) * a(7) - a(3) * a(4);
  const b03 = a(1) * a(6) - a(2) * a(5);
  const b04 = a(1) * a(7) - a(3) * a(5);
  const b05 = a(2) * a(7) - a(3) * a(6);
  const b06 = a(8) * a(13) - a(9) * a(12);
  const b07 = a(8) * a(14) - a(10) * a(12);
  const b08 = a(8) * a(15) - a(11) * a(12);
  const b09 = a(9) * a(14) - a(10) * a(13);
  const b10 = a(9) * a(15) - a(11) * a(13);
  const b11 = a(10) * a(15) - a(11) * a(14);
  const det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (Math.abs(det) < 1e-300) throw new FamilyCanonRefusal(['a node transform is singular — nothing can be re-expressed under it']);
  const d = 1 / det;
  const o = new Float64Array(16);
  o[0] = (a(5) * b11 - a(6) * b10 + a(7) * b09) * d;
  o[1] = (a(2) * b10 - a(1) * b11 - a(3) * b09) * d;
  o[2] = (a(13) * b05 - a(14) * b04 + a(15) * b03) * d;
  o[3] = (a(10) * b04 - a(9) * b05 - a(11) * b03) * d;
  o[4] = (a(6) * b08 - a(4) * b11 - a(7) * b07) * d;
  o[5] = (a(0) * b11 - a(2) * b08 + a(3) * b07) * d;
  o[6] = (a(14) * b02 - a(12) * b05 - a(15) * b01) * d;
  o[7] = (a(8) * b05 - a(10) * b02 + a(11) * b01) * d;
  o[8] = (a(4) * b10 - a(5) * b08 + a(7) * b06) * d;
  o[9] = (a(1) * b08 - a(0) * b10 - a(3) * b06) * d;
  o[10] = (a(12) * b04 - a(13) * b02 + a(15) * b00) * d;
  o[11] = (a(9) * b02 - a(8) * b04 - a(11) * b00) * d;
  o[12] = (a(5) * b07 - a(4) * b09 - a(6) * b06) * d;
  o[13] = (a(0) * b09 - a(1) * b07 + a(2) * b06) * d;
  o[14] = (a(13) * b01 - a(12) * b03 - a(14) * b00) * d;
  o[15] = (a(8) * b03 - a(9) * b01 + a(10) * b00) * d;
  return o;
}

function translationOf(m: Mat4): Vec3Data {
  return { x: m[12]!, y: m[13]!, z: m[14]! };
}

function decompose(m: Mat4): { t: Vec3Data; r: Quat; s: Vec3Data } {
  const cx = { x: m[0]!, y: m[1]!, z: m[2]! };
  const cy = { x: m[4]!, y: m[5]!, z: m[6]! };
  const cz = { x: m[8]!, y: m[9]!, z: m[10]! };
  let sx = vec3Length(cx);
  const sy = vec3Length(cy);
  const sz = vec3Length(cz);
  if (vec3Dot(vec3Cross(cx, cy), cz) < 0) sx = -sx;
  const r = quatFromBasis(
    { x: cx.x / sx, y: cx.y / sx, z: cx.z / sx },
    { x: cy.x / sy, y: cy.y / sy, z: cy.z / sy },
    { x: cz.x / sz, y: cz.y / sz, z: cz.z / sz },
  );
  return { t: translationOf(m), r, s: { x: sx, y: sy, z: sz } };
}

function transformPoint(m: Mat4, p: Vec3Data): Vec3Data {
  return {
    x: m[0]! * p.x + m[4]! * p.y + m[8]! * p.z + m[12]!,
    y: m[1]! * p.x + m[5]! * p.y + m[9]! * p.z + m[13]!,
    z: m[2]! * p.x + m[6]! * p.y + m[10]! * p.z + m[14]!,
  };
}

function localMatrix(n: GltfNode): Mat4 {
  if (n.matrix !== undefined) return Float64Array.from(n.matrix);
  const t = n.translation ?? [0, 0, 0];
  const r = n.rotation ?? [0, 0, 0, 1];
  const s = n.scale ?? [1, 1, 1];
  return compose({ x: t[0]!, y: t[1]!, z: t[2]! }, quatNormalize({ x: r[0]!, y: r[1]!, z: r[2]!, w: r[3]! }), { x: s[0]!, y: s[1]!, z: s[2]! });
}

/**
 * The rotation carrying the anchor's limb frame onto the body's: primary
 * along the bone (unit `a` → unit `b`), secondary the flesh's forward — world
 * forward (+Z, or the body's own facing) made square to the bone, or the
 * lateral axis where either bone runs forward (a foot), so both sides always
 * speak the same reference. Where the
 * limbs lie in the body's coronal plane this IS the minimal swing; where a
 * rest pose bends forward it keeps the flesh's front facing front instead of
 * letting a swing about a tilted axis roll it.
 */
export function limbFrameMap(
  a: Vec3Data,
  b: Vec3Data,
  forward: Vec3Data = { x: 0, y: 0, z: 1 },
  lateral: Vec3Data = { x: 1, y: 0, z: 0 },
): Quat {
  const ref = Math.abs(vec3Dot(a, forward)) > 0.7 || Math.abs(vec3Dot(b, forward)) > 0.7 ? lateral : forward;
  const frame = (u: Vec3Data): Quat => {
    const d = vec3Dot(ref, u);
    const f = vec3Normalize({ x: ref.x - u.x * d, y: ref.y - u.y * d, z: ref.z - u.z * d });
    return quatFromBasis(u, f, vec3Cross(u, f));
  };
  return quatNormalize(quatMultiply(frame(b), quatConjugate(frame(a))));
}

function rotate(q: Quat, v: Vec3Data): Vec3Data {
  return transformPoint(compose({ x: 0, y: 0, z: 0 }, q, { x: 1, y: 1, z: 1 }), v);
}

const side = (n: string): 'L' | 'R' | 'C' => (/^(L_|Left)/.test(n) ? 'L' : /^(R_|Right)/.test(n) ? 'R' : 'C');

// ───────────────────────────── the transform ─────────────────────────────

export interface FamilyCanonReport {
  readonly family: string;
  readonly renamed: readonly (readonly [string, string])[];
  readonly supplied: readonly string[];
  /** Every family bone's world-rotation change, degrees (largest first). */
  readonly reseated: readonly { readonly bone: string; readonly degrees: number }[];
  /** How far `Root` moved to sit on the ground below the hips, in the file's world units. */
  readonly rootMoved: number;
  /** Colliders whose offsets were re-expressed in a re-seated bone's frame. */
  readonly collidersReexpressed: number;
  /**
   * `native` — the body already speaks the anchor's frame convention (its keyed
   * bones' median re-seat is within CANON_RESEAT_MEDIAN_DEG): its frames are
   * kept, only names, Root and supplied bones change. `reseated` — a rig
   * preset's other roll: every family bone takes the canonical frame.
   */
  readonly convention: 'native' | 'reseated';
  /** The keyed bones' median rotation from their own bind frame to the canonical one, degrees. */
  readonly medianReseatDegrees: number;
  /**
   * The body's yaw about world up relative to the anchor (left thigh → right
   * thigh), degrees: the canon is yaw-free — the anchor is turned to face as
   * the body does (the goblin's whole skeleton stands 94° round, and World
   * animates it so today).
   */
  readonly yawDegrees: number;
}

/**
 * Above this median re-seat the body is off the family's convention, degrees.
 * Measured 2026-09-24: native amari 1.5, goblin 4.3 (the field-accepted class), the
 * ActorCore kobold 124.7 and the Mixamo-preset tiger 117.2.
 */
export const CANON_RESEAT_MEDIAN_DEG = 30;


export function canonicalizeForFamily(
  glb: ArrayBuffer,
  ontologyOf: ReadonlyMap<string, string>,
  canon: FamilyCanon,
): { glb: ArrayBuffer; report: FamilyCanonReport } {
  const { json, bin: binIn } = parseGlb(glb);
  const nodes = (json['nodes'] as GltfNode[] | undefined) ?? [];
  const skins = (json['skins'] as GltfSkin[] | undefined) ?? [];
  const accessors = (json['accessors'] as GltfAccessor[] | undefined) ?? [];
  const bufferViews = (json['bufferViews'] as GltfBufferView[] | undefined) ?? [];
  if (skins.length === 0) throw new FamilyCanonRefusal(['the body has no skin — there is no skeleton to canonicalize']);

  const parentOf = new Map<number, number>();
  nodes.forEach((n, i) => (n.children ?? []).forEach((c) => parentOf.set(c, i)));
  const jointSet = new Set<number>(skins.flatMap((s) => s.joints));
  const joints = [...jointSet];

  // ---- the plan, on the FILE's joints ----
  const plan = planFamilyRename(
    joints.map((i) => {
      const p = parentOf.get(i);
      return { name: nodes[i]!.name ?? `#${i}`, parent: p === undefined ? null : (nodes[p]!.name ?? `#${p}`) };
    }),
    ontologyOf,
    canon,
  );
  if (plan.refusals.length > 0) throw new FamilyCanonRefusal(plan.refusals);

  // ---- world matrices before anything moves ----
  const worldOld: Mat4[] = [];
  const worldOf = (i: number): Mat4 => {
    const hit = worldOld[i];
    if (hit !== undefined) return hit;
    const p = parentOf.get(i);
    const w = p === undefined ? localMatrix(nodes[i]!) : mul(worldOf(p), localMatrix(nodes[i]!));
    worldOld[i] = w;
    return w;
  };
  nodes.forEach((_, i) => worldOf(i));
  const oldCount = nodes.length;

  // ---- rename the joints ----
  const renamed: [string, string][] = [];
  for (const i of joints) {
    const n = nodes[i]!;
    const to = n.name === undefined ? undefined : plan.rename.get(n.name);
    if (to !== undefined) {
      renamed.push([n.name!, to]);
      n.name = to;
    }
  }
  const jointNames = joints.map((i) => nodes[i]!.name);
  const dupes = jointNames.filter((n, k) => n !== undefined && jointNames.indexOf(n) !== k);
  if (dupes.length > 0) throw new FamilyCanonRefusal([`two joints share the name(s) ${[...new Set(dupes)].join(', ')} after the rename`]);
  /** Name → node, joints winning over any other node that happens to share a name. */
  const byName = new Map<string, number>();
  nodes.forEach((n, i) => {
    if (n.name !== undefined && (jointSet.has(i) || !byName.has(n.name))) byName.set(n.name, i);
  });

  // ---- the anchor's world (clip space: Root at the top) ----
  const anchorRow = new Map(canon.anchor.map((r) => [r[0], r]));
  const anchorChildren = new Map<string, string[]>();
  for (const [name, parent] of canon.anchor) {
    if (parent !== null) anchorChildren.set(parent, [...(anchorChildren.get(parent) ?? []), name]);
  }
  const identity4 = compose({ x: 0, y: 0, z: 0 }, { x: 0, y: 0, z: 0, w: 1 }, { x: 1, y: 1, z: 1 });
  // The body's yaw about world up relative to the anchor, from the thighs (left → right).
  const rawAnchor = (name: string): Vec3Data => {
    let m = identity4;
    for (let n: string | null = name; n !== null; ) {
      const row: AnchorRow = anchorRow.get(n)!;
      const [, parent, t, r] = row;
      m = mul(compose({ x: t[0], y: t[1], z: t[2] }, quatNormalize({ x: r[0], y: r[1], z: r[2], w: r[3] }), { x: 1, y: 1, z: 1 }), m);
      n = parent;
    }
    return translationOf(m);
  };
  const jointPos = (n: string): Vec3Data => {
    const i = joints.find((j) => nodes[j]!.name === n);
    if (i === undefined) throw new FamilyCanonRefusal([`the rig has no "${n}" to measure the body against the anchor`]);
    return translationOf(worldOld[i]!);
  };
  const aLat = vec3Sub(rawAnchor('L_Thigh'), rawAnchor('R_Thigh'));
  const bLat = vec3Sub(jointPos('L_Thigh'), jointPos('R_Thigh'));
  const yawRad = Math.atan2(aLat.z, aLat.x) - Math.atan2(bLat.z, bLat.x);
  const yawQ = quatNormalize({ x: 0, y: Math.sin(yawRad / 2), z: 0, w: Math.cos(yawRad / 2) });
  const yawM = compose({ x: 0, y: 0, z: 0 }, yawQ, { x: 1, y: 1, z: 1 });
  const forward = transformPoint(yawM, { x: 0, y: 0, z: 1 });
  const lateral = transformPoint(yawM, { x: 1, y: 0, z: 0 });
  const anchorWorld = new Map<string, Mat4>();
  const anchorWorldOf = (name: string): Mat4 => {
    const hit = anchorWorld.get(name);
    if (hit !== undefined) return hit;
    const [, parent, t, r] = anchorRow.get(name)!;
    const local = compose({ x: t[0], y: t[1], z: t[2] }, quatNormalize({ x: r[0], y: r[1], z: r[2], w: r[3] }), { x: 1, y: 1, z: 1 });
    const w = parent === null ? mul(yawM, local) : mul(anchorWorldOf(parent), local);
    anchorWorld.set(name, w);
    return w;
  };
  const aPos = (n: string): Vec3Data => translationOf(anchorWorldOf(n));
  const aRot = (n: string): Quat => decompose(anchorWorldOf(n)).r;
  const anchorOrder: string[] = [];
  const visit = (n: string): void => {
    anchorOrder.push(n);
    for (const c of anchorChildren.get(n) ?? []) visit(c);
  };
  for (const [name, parent] of canon.anchor) if (parent === null) visit(name);

  // ---- body positions (world), facing and scale ----
  const pos = new Map<string, Vec3Data>();
  for (const [name, i] of byName) pos.set(name, translationOf(worldOld[i]!));
  const need = (n: string): Vec3Data => {
    const p = pos.get(n);
    if (p === undefined) throw new FamilyCanonRefusal([`the rig has no "${n}" to measure the body against the anchor`]);
    return p;
  };
  const legLen = (p: (n: string) => Vec3Data): number =>
    ['L', 'R'].reduce((s, k) => s + vec3Length(vec3Sub(p(`${k}_Calf`), p(`${k}_Thigh`))) + vec3Length(vec3Sub(p(`${k}_Foot`), p(`${k}_Calf`))), 0);
  const scale = legLen(need) / legLen(aPos);

  // ---- the mesh's bind (world): supplied joints' inverse binds and the ground ----
  const hipIdx = byName.get('Hip')!;
  const ibmOf = (skin: GltfSkin): Float32Array | null => {
    if (skin.inverseBindMatrices === undefined) return null;
    const acc = accessors[skin.inverseBindMatrices]!;
    const bv = bufferViews[acc.bufferView!]!;
    const start = (bv.byteOffset ?? 0) + (acc.byteOffset ?? 0);
    return new Float32Array(binIn.slice(start, start + acc.count * 64).buffer);
  };
  const hipSkin = skins.find((s) => s.joints.includes(hipIdx))!;
  const hipIbm = ibmOf(hipSkin);
  const kHip = hipSkin.joints.indexOf(hipIdx);
  const meshBind = hipIbm === null ? identity4 : mul(worldOld[hipIdx]!, Float64Array.from(hipIbm.subarray(kHip * 16, kHip * 16 + 16)));
  let ground = Infinity;
  const meshes = (json['meshes'] as { primitives: { attributes: Record<string, number> }[] }[] | undefined) ?? [];
  for (const n of nodes) {
    if (n.mesh === undefined || n.skin === undefined) continue;
    for (const prim of meshes[n.mesh]?.primitives ?? []) {
      const acc = accessors[prim.attributes['POSITION'] ?? -1];
      if (acc?.min === undefined || acc.max === undefined) continue;
      for (let c = 0; c < 8; c++) {
        const p = { x: c & 1 ? acc.max[0]! : acc.min[0]!, y: c & 2 ? acc.max[1]! : acc.min[1]!, z: c & 4 ? acc.max[2]! : acc.min[2]! };
        ground = Math.min(ground, transformPoint(meshBind, p).y);
      }
    }
  }
  if (!Number.isFinite(ground)) ground = Math.min(...joints.map((i) => translationOf(worldOld[i]!).y));

  // ---- supply the missing, unkeyed signature bones (positions first) ----
  const supplied = new Set(plan.supplied);
  const newIndex = new Map<string, number>();
  const addNode = (name: string, parentIdx: number | undefined, adopt: number[]): number => {
    const i = nodes.length;
    nodes.push({ name });
    if (parentIdx !== undefined) {
      const p = nodes[parentIdx]!;
      p.children = [...(p.children ?? []).filter((c) => !adopt.includes(c)), i];
      parentOf.set(i, parentIdx);
    } else {
      for (const s of (json['scenes'] as { nodes?: number[] }[] | undefined) ?? []) {
        if (s.nodes?.some((c) => adopt.includes(c)) === true) s.nodes = [...s.nodes.filter((c) => !adopt.includes(c)), i];
      }
    }
    if (adopt.length > 0) nodes[i]!.children = [...adopt];
    for (const a of adopt) parentOf.set(a, i);
    newIndex.set(name, i);
    byName.set(name, i);
    return i;
  };
  const deferredLeaves = new Set<string>();
  for (const m of anchorOrder) {
    if (!supplied.has(m)) continue;
    const [, pa] = anchorRow.get(m)!;
    if (pa === null) {
      const hp = need('Hip');
      addNode(m, parentOf.get(hipIdx), [hipIdx]);
      pos.set(m, { x: hp.x, y: ground, z: hp.z });
      continue;
    }
    const paIdx = byName.get(pa);
    if (paIdx === undefined) throw new FamilyCanonRefusal([`"${m}" hangs from "${pa}", which the rig does not carry`]);
    const kids = (anchorChildren.get(m) ?? []).filter((c) => byName.has(c) && !supplied.has(c));
    if (kids.length > 0) {
      const kidIdx = kids.map((c) => byName.get(c)!);
      if (kidIdx.some((c) => parentOf.get(c) !== paIdx)) {
        throw new FamilyCanonRefusal([`"${m}" belongs between "${pa}" and ${kids.map((c) => `"${c}"`).join(', ')}, but the rig parents them elsewhere`]);
      }
      const c0 = kids[0]!;
      const f = vec3Length(vec3Sub(aPos(m), aPos(pa))) / Math.max(1e-9, vec3Length(vec3Sub(aPos(c0), aPos(pa))));
      const pP = pos.get(pa)!;
      const pC = pos.get(c0)!;
      addNode(m, paIdx, kidIdx);
      pos.set(m, { x: pP.x + f * (pC.x - pP.x), y: pP.y + f * (pC.y - pP.y), z: pP.z + f * (pC.z - pP.z) });
    } else {
      addNode(m, paIdx, []);
      deferredLeaves.add(m);
    }
  }

  // ---- the canonical world rotation of every family joint ----
  const isFamilyJoint = (name: string): boolean => {
    const i = byName.get(name);
    return i !== undefined && (jointSet.has(i) || newIndex.get(name) === i);
  };
  const keyed = new Set(canon.keyed);
  const frameMap = new Map<string, Quat>();
  const canonRot = new Map<string, Quat>();
  for (const b of anchorOrder) {
    const [, pa] = anchorRow.get(b)!;
    const inherited = (pa === null ? undefined : frameMap.get(pa)) ?? { x: 0, y: 0, z: 0, w: 1 };
    let s = inherited;
    if (keyed.has(b) && pos.has(b)) {
      const cands = (anchorChildren.get(b) ?? []).filter((c) => pos.has(c) && !deferredLeaves.has(c));
      const sameSide = cands.filter((c) => side(c) === side(b));
      let best: string | null = null;
      let bestLen = 1e-4;
      for (const c of sameSide.length > 0 ? sameSide : cands) {
        const la = vec3Length(vec3Sub(aPos(c), aPos(b)));
        if (la > bestLen && vec3Length(vec3Sub(pos.get(c)!, pos.get(b)!)) > 1e-4) {
          best = c;
          bestLen = la;
        }
      }
      if (best !== null) s = limbFrameMap(vec3Normalize(vec3Sub(aPos(best), aPos(b))), vec3Normalize(vec3Sub(pos.get(best)!, pos.get(b)!)), forward, lateral);
    }
    frameMap.set(b, s);
    if (isFamilyJoint(b)) canonRot.set(b, quatNormalize(quatMultiply(s, aRot(b))));
    // A supplied leaf sits where the anchor's does, carried by its parent's frame and the body's scale.
    if (deferredLeaves.has(b) && pa !== null) {
      const pP = pos.get(pa)!;
      const off = rotate(inherited, vec3Sub(aPos(b), aPos(pa)));
      pos.set(b, { x: pP.x + off.x * scale, y: pP.y + off.y * scale, z: pP.z + off.z * scale });
    }
  }

  // ---- a body already speaking the anchor's convention keeps its own frames ----
  // Native Tripo bodies differ from the anchor only by their own rest pose (a
  // few degrees — amari's worst keyed bone 26.6°, median 1.5°); a rig
  // preset speaks another roll outright (kobold 124.7°, tiger 117.2°). The
  // first class is field-accepted as it plays today, so its frames stay; only
  // a body off the convention is re-seated.
  const oldRot = (b: string): Quat => decompose(worldOld[byName.get(b)!]!).r;
  const keyedChanges = [...canonRot]
    .filter(([b]) => keyed.has(b) && !newIndex.has(b))
    .map(([b, r]) => (quatAngleBetween(oldRot(b), r) * 180) / Math.PI)
    .sort((x, y) => x - y);
  const medianReseat = keyedChanges.length === 0 ? 0 : keyedChanges[Math.floor(keyedChanges.length / 2)]!;
  const convention: FamilyCanonReport['convention'] = medianReseat > CANON_RESEAT_MEDIAN_DEG ? 'reseated' : 'native';
  if (convention === 'native') {
    for (const b of anchorOrder) {
      if (!canonRot.has(b)) continue;
      const [, pa] = anchorRow.get(b)!;
      if (!newIndex.has(b)) {
        canonRot.set(b, oldRot(b));
        continue;
      }
      if (pa === null) continue;
      // Supplied: the anchor's local rest under the body's own parent frame.
      const parentRot = canonRot.get(pa) ?? oldRot(pa);
      canonRot.set(b, quatNormalize(quatMultiply(parentRot, quatMultiply(quatConjugate(aRot(pa)), aRot(b)))));
      if (deferredLeaves.has(b)) {
        const pP = pos.get(pa)!;
        const off = rotate(quatMultiply(parentRot, quatConjugate(aRot(pa))), vec3Sub(aPos(b), aPos(pa)));
        pos.set(b, { x: pP.x + off.x * scale, y: pP.y + off.y * scale, z: pP.z + off.z * scale });
      }
    }
  }

  // ---- new world matrices (a non-family node keeps its world) ----
  const worldNew: Mat4[] = [];
  const reseated: { bone: string; degrees: number }[] = [];
  let rootMoved = 0;
  const uniformScale = (m: Mat4): number => {
    const s = decompose(m).s;
    return (Math.abs(s.x) + Math.abs(s.y) + Math.abs(s.z)) / 3;
  };
  nodes.forEach((n, i) => {
    const r = n.name === undefined ? undefined : canonRot.get(n.name);
    const keepsOwn = convention === 'native' && i < oldCount && n.name !== 'Root';
    if (r === undefined || byName.get(n.name!) !== i || keepsOwn) {
      worldNew[i] = worldOld[i]!;
      return;
    }
    const parentIdx = parentOf.get(i);
    const sc = i < oldCount ? uniformScale(worldOld[i]!) : parentIdx !== undefined ? uniformScale(worldOld[parentIdx] ?? identity4) : 1;
    let at = pos.get(n.name!)!;
    if (n.name === 'Root') {
      const hp = need('Hip');
      at = { x: hp.x, y: ground, z: hp.z };
      if (i < oldCount) rootMoved = vec3Length(vec3Sub(at, translationOf(worldOld[i]!)));
    }
    worldNew[i] = compose(at, r, { x: sc, y: sc, z: sc });
    if (i < oldCount) reseated.push({ bone: n.name!, degrees: (quatAngleBetween(decompose(worldOld[i]!).r, r) * 180) / Math.PI });
  });

  // ---- locals, from the new worlds (only where a world or a parent changed) ----
  const clean = (v: number): number => (Math.abs(v) < 1e-15 ? 0 : v);
  nodes.forEach((n, i) => {
    const p = parentOf.get(i);
    const changed = i >= oldCount || worldNew[i] !== worldOld[i] || (p !== undefined && (p >= oldCount || worldNew[p] !== worldOld[p]));
    if (!changed) return;
    const local = p === undefined ? worldNew[i]! : mul(invert(worldNew[p]!), worldNew[i]!);
    const { t, r, s } = decompose(local);
    delete n.matrix;
    n.translation = [clean(t.x), clean(t.y), clean(t.z)];
    n.rotation = [clean(r.x), clean(r.y), clean(r.z), clean(r.w)];
    const near1 = (v: number): boolean => Math.abs(v - 1) < 1e-9;
    if (near1(s.x) && near1(s.y) && near1(s.z)) delete n.scale;
    else n.scale = [s.x, s.y, s.z];
  });

  // ---- inverse binds: the mesh at rest is unchanged ----
  let bin = new Uint8Array(binIn);
  const appendView = (bytes: Uint8Array): number => {
    const pad = (4 - (bin.length % 4)) % 4;
    const out = new Uint8Array(bin.length + pad + bytes.length);
    out.set(bin, 0);
    out.set(bytes, bin.length + pad);
    bufferViews.push({ buffer: 0, byteOffset: bin.length + pad, byteLength: bytes.length });
    bin = out;
    return bufferViews.length - 1;
  };
  const suppliedIdx = plan.supplied.map((m) => newIndex.get(m)!);
  for (const skin of skins) {
    const old = ibmOf(skin);
    const add = skin.joints.includes(hipIdx) ? suppliedIdx : [];
    const jointsNew = [...skin.joints, ...add];
    const out = new Float32Array(jointsNew.length * 16);
    jointsNew.forEach((j, k) => {
      const was = old === null || k >= skin.joints.length ? null : Float64Array.from(old.subarray(k * 16, k * 16 + 16));
      out.set(was === null ? mul(invert(worldNew[j]!), meshBind) : mul(mul(invert(worldNew[j]!), worldOld[j]!), was), k * 16);
    });
    if (add.length === 0 && skin.inverseBindMatrices !== undefined) {
      const acc = accessors[skin.inverseBindMatrices]!;
      const bv = bufferViews[acc.bufferView!]!;
      bin.set(new Uint8Array(out.buffer), (bv.byteOffset ?? 0) + (acc.byteOffset ?? 0));
      // The exporter's bounds describe the OLD matrices; they are optional on an inverse-bind accessor.
      delete acc.min;
      delete acc.max;
    } else {
      const view = appendView(new Uint8Array(out.buffer));
      accessors.push({ bufferView: view, componentType: 5126, count: jointsNew.length, type: 'MAT4' });
      skin.inverseBindMatrices = accessors.length - 1;
      skin.joints = jointsNew;
    }
    // A supplied Root sits above the old skeleton root: it is the joints' common root now.
    const rootIdx = newIndex.get('Root');
    if (rootIdx !== undefined && skin.skeleton !== undefined && skin.joints.includes(rootIdx)) skin.skeleton = rootIdx;
  }
  json['accessors'] = accessors;
  json['bufferViews'] = bufferViews;
  const buffers = json['buffers'] as { byteLength: number }[] | undefined;
  if (buffers?.[0] !== undefined) buffers[0].byteLength = bin.length;

  // ---- the spring's colliders ride their bones' new frames ----
  let collidersReexpressed = 0;
  type Shape = { sphere?: { offset: number[] }; capsule?: { offset: number[]; tail: number[] } };
  const ext = (json['extensions'] as Json | undefined)?.['VRMC_springBone'] as { colliders?: { node: number; shape: Shape }[] } | undefined;
  for (const c of ext?.colliders ?? []) {
    if (worldNew[c.node] === worldOld[c.node]) continue;
    const toNew = mul(invert(worldNew[c.node]!), worldOld[c.node]!);
    const re = (v: number[]): number[] => {
      const p = transformPoint(toNew, { x: v[0]!, y: v[1]!, z: v[2]! });
      return [p.x, p.y, p.z];
    };
    if (c.shape.sphere !== undefined) c.shape.sphere.offset = re(c.shape.sphere.offset);
    if (c.shape.capsule !== undefined) {
      c.shape.capsule.offset = re(c.shape.capsule.offset);
      c.shape.capsule.tail = re(c.shape.capsule.tail);
    }
    collidersReexpressed += 1;
  }

  reseated.sort((a, b) => b.degrees - a.degrees);
  return {
    glb: buildGlb(json, bin),
    report: {
      family: canon.id,
      renamed,
      supplied: plan.supplied,
      reseated: convention === 'native' ? [] : reseated,
      rootMoved,
      collidersReexpressed,
      convention,
      medianReseatDegrees: medianReseat,
      yawDegrees: (((((yawRad * 180) / Math.PI) % 360) + 540) % 360) - 180,
    },
  };
}
